import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Database, LocateFixed, Rows3 } from 'lucide-react'
import type { SchemaNodeRecord } from '@/frontend/features/Fde/ontologySchemaProjection'
import type { FdeCopy } from '@/frontend/features/Fde/types'

interface Props {
  node: SchemaNodeRecord
  x: number
  y: number
  canGoBack: boolean
  copy: FdeCopy
  onFocus: () => void
  onOpenElements: () => void
  onOpenData: () => void
  onBack: () => void
  onClose: () => void
}

export default function OntologySchemaContextMenu(props: Props) {
  useEffect(() => {
    function closeOutside(event: PointerEvent) {
      if (event.target instanceof Element && event.target.closest('[data-schema-context-menu]')) return
      props.onClose()
    }
    function closeFromKeyboard(event: KeyboardEvent) { if (event.key === 'Escape') props.onClose() }
    window.addEventListener('pointerdown', closeOutside)
    window.addEventListener('keydown', closeFromKeyboard)
    window.addEventListener('resize', props.onClose)
    return () => {
      window.removeEventListener('pointerdown', closeOutside)
      window.removeEventListener('keydown', closeFromKeyboard)
      window.removeEventListener('resize', props.onClose)
    }
  }, [props.onClose])

  const elementLabel = props.node.elementCount === 1 ? props.copy.workspace.element : props.copy.workspace.elements.toLocaleLowerCase()
  return createPortal(
    <div
      role="menu"
      aria-label={props.copy.workspace.schema_node_actions}
      data-schema-context-menu
      className="fixed z-[100] w-52 rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-xl animate-in fade-in-0 zoom-in-95"
      style={{ left: props.x, top: props.y }}
    >
      <MenuItem icon={LocateFixed} label={props.copy.workspace.schema_focus_node} onSelect={props.onFocus} />
      {props.node.kind === 'class' && <MenuItem icon={Rows3} label={`${props.node.elementCount} ${elementLabel}`} onSelect={props.onOpenElements} />}
      {props.node.kind === 'class' && props.node.backingCount > 0 && <MenuItem icon={Database} label={props.copy.workspace.schema_open_data} onSelect={props.onOpenData} />}
      {props.canGoBack && <><div className="my-1 h-px bg-border" /><MenuItem icon={ArrowLeft} label={props.copy.workspace.schema_back_previous} onSelect={props.onBack} /></>}
    </div>,
    document.body,
  )
}

function MenuItem({ icon: Icon, label, onSelect }: { icon: typeof LocateFixed; label: string; onSelect: () => void }) {
  return <button type="button" role="menuitem" onClick={onSelect} className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-xs outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"><Icon className="size-3.5 text-muted-foreground" /><span className="truncate">{label}</span></button>
}
